/**
 * Jahresabschluss presentation model — the catalog of the 24 ratified positions
 * plus the pure functions that turn a flat `key → value` map into the four
 * things a reader actually wants: headline KPIs, the Bilanz as a tree, the GuV
 * as a statement, and the derived Kennzahlen.
 *
 * Framework-independent by design (BDA-5849): the very same statement is
 * rendered from two completely different wires —
 *
 * - the clinic browser's `GET /clinics/{clinicSn}/annual-account`, which ships
 *   the 24 positions as `{ key, label, value }` triples, and
 * - a Virtual Clinic's captured `parts.aktiva / .passiva / .guv`, which are flat
 *   objects keyed by those same field names and carry NO labels at all.
 *
 * Both collapse to a `key → number | null` map, so everything below is written
 * against that map and nothing else.
 *
 * ## Why a hand-written catalog rather than key parsing
 *
 * The keys look parseable (`aa` → `aai` → `aai1`), but they are not: `i` is both
 * a roman numeral and a plain letter suffix, and the full capture schema goes as
 * deep as `abii3aa`. Guessing hierarchy from the string would be wrong for the
 * positions that matter. The clinic endpoint ships an explicitly curated subset
 * of exactly 24 positions, only two levels deep, so the tree is small enough to
 * state outright — and stating it is the only way it can be trusted.
 *
 * ## Two catalogs, because the backend publishes two sets
 *
 * {@link AYS_ANNUAL_ACCOUNT_POSITIONS} holds the 24 curated, BENCHMARKABLE
 * positions; {@link AYS_ANNUAL_ACCOUNT_BALANCE_SHEET_POSITIONS} holds the eight
 * display-only ones that close `Summe Aktiva` / `Summe Passiva` (BDA-6306).
 * Both are rendered into the Bilanz, and only the first may be offered anywhere
 * a Kennzahl is picked. {@link AYS_ANNUAL_ACCOUNT_STATEMENT_KEYS} is the union,
 * and is what a caller reading a flat capture object must use.
 */

/** Which statement a position belongs to. */
export type AysAnnualAccountGroup = 'AKTIVA' | 'PASSIVA' | 'GUV';

/**
 * How a row reads, which is a display concern rather than a maths one — every
 * value is used exactly as the backend sends it.
 *
 * - `POSITION` — an ordinary line of the statement.
 * - `EXPENSE` — an Aufwand line. The wire sends these as POSITIVE magnitudes
 *   (German Staffelform lists Aufwendungen positive and subtracts implicitly),
 *   so they are flagged rather than negated: inventing a minus sign would
 *   misreport the filed figure.
 * - `SUBTOTAL` — a running result (EBITDA / EBIT / EBT). Emphasised.
 * - `TOTAL` — the closing line of its group (Summe Aktiva / Passiva, and the
 *   Jahresüberschuss for the GuV). Emphasised and rule-topped.
 */
export type AysAnnualAccountRole = 'POSITION' | 'EXPENSE' | 'SUBTOTAL' | 'TOTAL';

/** One catalog entry: a position's identity, independent of any one clinic. */
export interface AysAnnualAccountPosition {
  key: string;
  /**
   * Fallback German label. The clinic endpoint ships its own curated label and
   * that one wins; this is what the Virtual Clinic wire (which has no labels)
   * renders, and the safety net for a clinic-api response with a blank label.
   */
  label: string;
  group: AysAnnualAccountGroup;
  role: AysAnnualAccountRole;
  /** Key of the position this one rolls up into, if any. */
  parent?: string;
}

/**
 * The 24 positions, in the ratified catalog order the endpoint answers in.
 *
 * Labels are verbatim from the epic backend with the redundant `" (EUR)"`
 * suffix dropped — the unit is stated once in the column header instead, per the
 * table styleguide ("units live in the column header").
 */
export const AYS_ANNUAL_ACCOUNT_POSITIONS: readonly AysAnnualAccountPosition[] =
  [
    { key: 'aa', label: 'A. Anlagevermögen', group: 'AKTIVA', role: 'POSITION' },
    {
      key: 'aai',
      label: 'I. Immaterielle Vermögensgegenstände',
      group: 'AKTIVA',
      role: 'POSITION',
      parent: 'aa',
    },
    {
      key: 'aaii',
      label: 'II. Sachanlagen',
      group: 'AKTIVA',
      role: 'POSITION',
      parent: 'aa',
    },
    {
      key: 'aaiii',
      label: 'III. Finanzanlagen',
      group: 'AKTIVA',
      role: 'POSITION',
      parent: 'aa',
    },
    { key: 'ab', label: 'B. Umlaufvermögen', group: 'AKTIVA', role: 'POSITION' },
    {
      key: 'abi',
      label: 'I. Vorräte',
      group: 'AKTIVA',
      role: 'POSITION',
      parent: 'ab',
    },
    {
      key: 'abii',
      label: 'II. Forderungen und sonstige Vermögensgegenstände',
      group: 'AKTIVA',
      role: 'POSITION',
      parent: 'ab',
    },
    {
      key: 'abiii',
      label: 'III. Wertpapiere des Umlaufvermögens',
      group: 'AKTIVA',
      role: 'POSITION',
      parent: 'ab',
    },
    {
      key: 'abiv',
      label: 'IV. Kassenbestand, Guthaben bei Kreditinstituten',
      group: 'AKTIVA',
      role: 'POSITION',
      parent: 'ab',
    },
    {
      key: 'summe_aktiva',
      label: 'Summe Aktiva',
      group: 'AKTIVA',
      role: 'TOTAL',
    },

    { key: 'pa', label: 'A. Eigenkapital', group: 'PASSIVA', role: 'POSITION' },
    {
      key: 'pb',
      label: 'B. Sonderposten aus Zuwendungen zur Finanzierung des Anlagevermögens',
      group: 'PASSIVA',
      role: 'POSITION',
    },
    { key: 'pc', label: 'C. Rückstellungen', group: 'PASSIVA', role: 'POSITION' },
    {
      key: 'pd',
      label: 'D. Verbindlichkeiten',
      group: 'PASSIVA',
      role: 'POSITION',
    },
    {
      key: 'summe_passiva',
      label: 'Summe Passiva',
      group: 'PASSIVA',
      role: 'TOTAL',
    },

    {
      key: 'g0',
      label: 'Summe Umsatzerlöse (ohne sonst. Erträge)',
      group: 'GUV',
      role: 'POSITION',
    },
    {
      key: 'g7',
      label: 'sonstige betriebliche Erträge',
      group: 'GUV',
      role: 'POSITION',
    },
    { key: 'g9', label: 'Personalaufwand', group: 'GUV', role: 'EXPENSE' },
    { key: 'g11', label: 'Materialaufwand', group: 'GUV', role: 'EXPENSE' },
    { key: 'g14', label: 'EBITDA', group: 'GUV', role: 'SUBTOTAL' },
    {
      key: 'g15',
      label:
        'Abschreibungen auf immaterielle Vermögensgegenstände des Anlagevermögens und Sachanlagen',
      group: 'GUV',
      role: 'EXPENSE',
    },
    { key: 'g17', label: 'EBIT', group: 'GUV', role: 'SUBTOTAL' },
    {
      key: 'g19',
      label: 'EBT (Ergebnis der gewöhnlichen Geschäftstätigkeit vor Steuern)',
      group: 'GUV',
      role: 'SUBTOTAL',
    },
    {
      key: 'g26',
      label: 'Jahresüberschuss/Jahresfehlbetrag',
      group: 'GUV',
      role: 'TOTAL',
    },
  ];

/**
 * The eight Bilanz positions that CLOSE the two Summen but are not
 * benchmarkable metrics (BDA-6306).
 *
 * Deliberately a second constant rather than eight more entries in
 * {@link AYS_ANNUAL_ACCOUNT_POSITIONS}: that array is the curated catalog of
 * positions the Kennzahlen-Benchmarking also publishes, and the backend drew
 * this line on purpose. No published Kennzahl references any of these eight, so
 * they are display-only — not operating numbers, not export columns, not
 * `?metric=` values — and nothing that reads the benchmarkable catalog (the
 * cube's Kennzahl tree, the metric picker) may pick them up by accident.
 *
 * ## Why they exist at all
 *
 * `Summe Aktiva` is `A + B + C + D + E + F + G`, and the read model carries only
 * A and B — so a share column measured against the Bilanzsumme reached about
 * 98 % and could never reach 100 %. It is not an exotic case: 8.162 of the
 * 8.467 clinic-years holding a Bilanz fall short of their own Aktiva total. The
 * gap was invisible while each level had its own denominator and became visible
 * once the column got one; these positions close it.
 *
 * ## The grouping is ours, because the wire has none
 *
 * The response ships them in the plain `{ key, label, value }` metric shape with
 * NO field naming the total they complete, so `group` here is the frontend's own
 * table — taken from `AnnualAccountBalanceSheetPositions.kt` on
 * `epic/bda-5849`, where the eight are defined. Labels match that file with its
 * `" (EUR)"` suffix dropped, and a label from the wire still wins at render
 * time; the three Passiva ones genuinely carry no letter prefix in the filed
 * account, so none is invented here.
 *
 * Order is the filed account's: the five Aktiva ones after B. Umlaufvermögen,
 * the three Passiva ones after D. Verbindlichkeiten, both before their Summe.
 */
export const AYS_ANNUAL_ACCOUNT_BALANCE_SHEET_POSITIONS: readonly AysAnnualAccountPosition[] =
  [
    {
      key: 'ac',
      label: 'C. Ausgleichsposten nach dem KHG',
      group: 'AKTIVA',
      role: 'POSITION',
    },
    {
      key: 'ad',
      label: 'D. Rechnungsabgrenzungsposten',
      group: 'AKTIVA',
      role: 'POSITION',
    },
    {
      key: 'ae',
      label: 'E. Aktive latente Steuern',
      group: 'AKTIVA',
      role: 'POSITION',
    },
    {
      key: 'af',
      label: 'F. Aktiver Unterschiedsbetrag aus der Vermögensverrechnung',
      group: 'AKTIVA',
      role: 'POSITION',
    },
    {
      key: 'ag',
      label: 'G. Nicht durch Eigenkapital gedeckter Fehlbetrag',
      group: 'AKTIVA',
      role: 'POSITION',
    },
    {
      key: 'pe',
      label: 'Passive latente Steuern',
      group: 'PASSIVA',
      role: 'POSITION',
    },
    {
      key: 'pf',
      label: 'Ausgleichsposten aus Darlehensförderung',
      group: 'PASSIVA',
      role: 'POSITION',
    },
    {
      key: 'pg',
      label: 'Rechnungsabgrenzungsposten',
      group: 'PASSIVA',
      role: 'POSITION',
    },
  ];

/** Every catalog key, for callers that need to read a flat capture object. */
export const AYS_ANNUAL_ACCOUNT_KEYS: readonly string[] =
  AYS_ANNUAL_ACCOUNT_POSITIONS.map((position) => position.key);

/**
 * Every key the rendered statement reads — the benchmarkable catalog PLUS the
 * eight closing Bilanz positions.
 *
 * This, not {@link AYS_ANNUAL_ACCOUNT_KEYS}, is what a caller building its value
 * map from a flat capture object must use: the statement emits a row for each of
 * the eight, so a caller that supplies only the 24 renders eight empty lines —
 * a worse Bilanz than the one before they existed.
 */
export const AYS_ANNUAL_ACCOUNT_STATEMENT_KEYS: readonly string[] = [
  ...AYS_ANNUAL_ACCOUNT_KEYS,
  ...AYS_ANNUAL_ACCOUNT_BALANCE_SHEET_POSITIONS.map((position) => position.key),
];

/** A position's amount, or `null` when it was not reported. */
export type AysAnnualAccountValues = Readonly<Record<string, number | null>>;

/** A rendered row of the Bilanz tree or the GuV statement. */
export interface AysAnnualAccountRow {
  key: string;
  label: string;
  role: AysAnnualAccountRole;
  /** `null` means "not reported" — never a zero. */
  value: number | null;
  /**
   * Share of the statement's own total, as a fraction in `[0, 1]`-ish (it can
   * exceed 1 or go negative, since filed figures are not guaranteed to be
   * tidy). `null` when it cannot be computed — see {@link share}.
   *
   * **One denominator for the whole column**, at every depth: the Bilanzsumme
   * for a Bilanz row, the Gesamtleistung for a GuV row. That is what makes the
   * column checkable — children add up to their parent and parents to the
   * total, both against rows on the same screen. It used to measure a child
   * against its own parent instead, which put two different denominators under
   * one header with only a footnote to tell them apart.
   *
   * The parent-relative reading is not lost, it moved to {@link parentShare}.
   */
  share: number | null;
  /**
   * This position measured against its immediate parent position — "II.
   * Sachanlagen are 99,5 % of the Anlagevermögen".
   *
   * `null` on a top-level row (which has no parent) and wherever the parent is
   * unreported or zero. Rendered as a quiet second line under {@link share},
   * so the hierarchy reading survives without the column changing meaning by
   * depth.
   */
  parentShare: number | null;
  /** Label of the position {@link parentShare} is measured against, if any. */
  parentLabel: string | null;
  /**
   * This position measured against the SAME position of a reference statement
   * — the Standortanteil, when the reference is the legal entity's whole filed
   * account (BDA-6154).
   *
   * Distinct from {@link share}, which compares a position to a total WITHIN
   * its own statement. `null` whenever no reference was supplied, the position
   * is unreported, or the reference does not report it either.
   *
   * Apportionment multiplies every position by one scalar, so on a real
   * apportioned filing this reads the same on every row — which is exactly what
   * makes the split legible line by line rather than asserted once.
   */
  referenceShare: number | null;
  /**
   * The SAME position's amount in the reference statement — the legal entity's
   * unsplit figure, when this statement is only a Standort's share of it.
   *
   * Rendered as a second amount column rather than by re-deriving a whole
   * second statement: because apportionment is proportional, every percentage
   * on the page is identical for both, so showing the entity's euros alongside
   * adds information without changing a single computed value.
   *
   * `null` whenever no reference was supplied or it does not report the
   * position.
   */
  referenceValue: number | null;
  children: AysAnnualAccountRow[];
}

/**
 * One of the six headline figures of the KPI band — Umsatzerlöse,
 * Personalaufwand, Materialaufwand, EBITDA, Jahresergebnis, Bilanzsumme, in
 * that order (BDA-6339).
 */
export interface AysAnnualAccountKpi {
  key: string;
  label: string;
  value: number | null;
  /**
   * The same headline figure in the reference statement — the legal entity's
   * unsplit filing — so the KPI band can follow the Standortanteil ⇄
   * Gesamtabschluss toggle without a second statement being derived.
   *
   * `null` when no reference was supplied or it does not report the position.
   */
  referenceValue: number | null;
}

/** One derived ratio, with the formula it was computed from. */
export interface AysAnnualAccountRatio {
  key: string;
  label: string;
  /** Fraction, not percent — `0.627`, not `62.7`. `null` when not computable. */
  value: number | null;
  /** Human-readable formula, surfaced as the row's tooltip. */
  formula: string;
}

/** Everything the Jahresabschluss view renders, derived in one pass. */
export interface AysAnnualAccountStatement {
  kpis: AysAnnualAccountKpi[];
  aktiva: AysAnnualAccountRow[];
  passiva: AysAnnualAccountRow[];
  guv: AysAnnualAccountRow[];
  ratios: AysAnnualAccountRatio[];
  /** True when not one rendered position — the 24 plus the eight — carries a number. */
  empty: boolean;
  /**
   * A reference statement was supplied, so {@link AysAnnualAccountRow.referenceShare}
   * carries a meaning and its column is worth rendering.
   */
  hasReference: boolean;
}

/**
 * Share of a reference total.
 *
 * Guards the three ways this goes wrong on real filings: a missing value, a
 * missing or zero denominator (division by zero yields `Infinity`, which would
 * render as a nonsense percentage), and a denominator that is itself unreported.
 */
function share(value: number | null, total: number | null): number | null {
  if (value == null || total == null || total === 0) return null;
  return value / total;
}

/** `value` unless it is missing — used where a ratio needs a real denominator. */
function reported(values: AysAnnualAccountValues, key: string): number | null {
  const value = values[key];
  return value == null ? null : value;
}

/**
 * Builds one group's rows, nesting children under their parent and computing
 * each row's `share` and `parentShare`.
 *
 * **One denominator for the whole `share` column**, at every depth — the group's
 * own total. A reader can then check the column against rows that are on the
 * same screen: the children of A. Anlagevermögen add up to A. Anlagevermögen,
 * and the top-level positions add up towards the Summe row.
 *
 * The sentence the old depth-dependent denominator produced ("II. Sachanlagen
 * 99,5 % — almost all of the Anlagevermögen is Sachanlagen") is worth keeping,
 * so it moved to its own field, `parentShare`, and is rendered as a quiet
 * second line instead of silently redefining the column.
 */
function buildGroup(
  group: AysAnnualAccountGroup,
  values: AysAnnualAccountValues,
  labels: Readonly<Record<string, string>>,
  total: number | null,
  reference: AysAnnualAccountValues | null,
): AysAnnualAccountRow[] {
  const positions = AYS_ANNUAL_ACCOUNT_POSITIONS.filter(
    (position) => position.group === group,
  );

  const labelOf = (position: AysAnnualAccountPosition): string =>
    labels[position.key] || position.label;

  const row = (
    position: AysAnnualAccountPosition,
    parent: AysAnnualAccountPosition | null,
  ): AysAnnualAccountRow => {
    const value = values[position.key] ?? null;
    const parentValue = parent === null ? null : values[parent.key] ?? null;
    return {
      key: position.key,
      label: labelOf(position),
      role: position.role,
      value,
      // The Summe row is 100 % of itself. It used to be blanked as saying
      // nothing; with one denominator for the column it is the anchor the rest
      // of the column is checked against, so it is printed.
      share: share(value, total),
      // Only a nested row has a parent to be measured against; a top-level one
      // would just restate `share`.
      parentShare: parent === null ? null : share(value, parentValue),
      parentLabel: parent === null ? null : labelOf(parent),
      // Unlike `share`, this one IS meaningful on a total row: "the Summe
      // Aktiva you see is 43,9 % of the entity's" is the whole point.
      referenceShare:
        reference === null ? null : share(value, reference[position.key] ?? null),
      referenceValue: reference === null ? null : reference[position.key] ?? null,
      children: positions
        .filter((child) => child.parent === position.key)
        .map((child) => row(child, position)),
    };
  };

  const topLevel = positions.filter((position) => position.parent === undefined);

  // The eight closing positions go where the filed account puts them: after the
  // last lettered position of their side, before its Summe. Expressed as
  // "everything that is not the TOTAL, then these, then the TOTAL" rather than
  // by splicing at an index, so it stays correct if the catalog gains a
  // position — and it is a no-op for the GuV, which has none of them.
  const ordered = [
    ...topLevel.filter((position) => position.role !== 'TOTAL'),
    ...AYS_ANNUAL_ACCOUNT_BALANCE_SHEET_POSITIONS.filter(
      (position) => position.group === group,
    ),
    ...topLevel.filter((position) => position.role === 'TOTAL'),
  ];

  return ordered.map((position) => row(position, null));
}

/**
 * Turns the flat `key → value` map into the full statement.
 *
 * `labels` lets a caller that has curated labels on the wire (the clinic-api
 * response) override the catalog's; callers without them (a Virtual Clinic's
 * capture object) pass nothing and get the catalog labels.
 *
 * `reference` is an optional second value map every position is additionally
 * measured against, position by position — the legal entity's whole filed
 * account, when this statement is only a Standort's share of it (BDA-6154).
 * Omit it and every `referenceShare` is `null` and `hasReference` is false, so
 * existing callers are untouched.
 */
export function buildAnnualAccountStatement(
  values: AysAnnualAccountValues,
  labels: Readonly<Record<string, string>> = {},
  reference: AysAnnualAccountValues | null = null,
): AysAnnualAccountStatement {
  const summeAktiva = reported(values, 'summe_aktiva');
  const summePassiva = reported(values, 'summe_passiva');
  const umsatz = reported(values, 'g0');
  const sonstigeErtraege = reported(values, 'g7');
  // Gesamtleistung — the denominator the margins are measured against, since a
  // hospital's sonstige betriebliche Erträge (Fördermittel, Auflösung von
  // Sonderposten) are a structural part of what it earns, not a one-off.
  const gesamtleistung =
    umsatz == null && sonstigeErtraege == null
      ? null
      : (umsatz ?? 0) + (sonstigeErtraege ?? 0);

  const label = (key: string): string =>
    labels[key] ||
    AYS_ANNUAL_ACCOUNT_POSITIONS.find((position) => position.key === key)
      ?.label ||
    key;

  const referenceOf = (key: string): number | null =>
    reference === null ? null : reference[key] ?? null;

  const kpi = (key: string, label: string): AysAnnualAccountKpi => ({
    key,
    label,
    value: values[key] ?? null,
    referenceValue: referenceOf(key),
  });

  // The order product asked for (BDA-6339): the GuV read top to bottom —
  // Umsatz, the two big cost blocks, EBITDA, the result — then the Bilanzsumme.
  const kpis: AysAnnualAccountKpi[] = [
    kpi('g0', 'Umsatzerlöse'),
    kpi('g9', 'Personalaufwand'),
    kpi('g11', 'Materialaufwand'),
    kpi('g14', 'EBITDA'),
    kpi('g26', 'Jahresergebnis'),
    { ...kpi('summe_aktiva', 'Bilanzsumme'), value: summeAktiva },
  ];

  const ratios: AysAnnualAccountRatio[] = [
    {
      key: 'ek-quote',
      label: 'Eigenkapitalquote',
      value: share(reported(values, 'pa'), summePassiva),
      formula: 'A. Eigenkapital ÷ Summe Passiva',
    },
    {
      key: 'anlagenintensitaet',
      label: 'Anlagenintensität',
      value: share(reported(values, 'aa'), summeAktiva),
      formula: 'A. Anlagevermögen ÷ Summe Aktiva',
    },
    {
      key: 'personalquote',
      label: 'Personalaufwandsquote',
      value: share(reported(values, 'g9'), gesamtleistung),
      formula: `${label('g9')} ÷ Gesamtleistung`,
    },
    {
      key: 'materialquote',
      label: 'Materialaufwandsquote',
      value: share(reported(values, 'g11'), gesamtleistung),
      formula: `${label('g11')} ÷ Gesamtleistung`,
    },
    {
      key: 'ebitda-marge',
      label: 'EBITDA-Marge',
      value: share(reported(values, 'g14'), gesamtleistung),
      formula: 'EBITDA ÷ Gesamtleistung',
    },
    {
      key: 'ebit-marge',
      label: 'EBIT-Marge',
      value: share(reported(values, 'g17'), gesamtleistung),
      formula: 'EBIT ÷ Gesamtleistung',
    },
  ];

  return {
    kpis,
    aktiva: buildGroup('AKTIVA', values, labels, summeAktiva, reference),
    passiva: buildGroup('PASSIVA', values, labels, summePassiva, reference),
    guv: buildGroup('GUV', values, labels, gesamtleistung, reference),
    ratios,
    empty: AYS_ANNUAL_ACCOUNT_STATEMENT_KEYS.every(
      (key) => values[key] == null,
    ),
    hasReference: reference !== null,
  };
}

/**
 * Drops the endpoint's redundant `" (EUR)"` label suffix.
 *
 * Every one of the 24 labels carries it, and the table states the unit once in
 * its column header — repeating it 24 times is the exact noise the styleguide's
 * "units live in the column header" rule exists to prevent. Applied
 * defensively (the suffix may disappear from the wire without notice).
 */
export function stripAnnualAccountUnitSuffix(label: string): string {
  return label.replace(/\s*\(EUR\)\s*$/, '');
}
